import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardContent } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { BackLink } from '@/components/DetailRow';
import { FORM_COLUMN } from '@/components/layout';
import { useCreatePromotion } from '@/data/hooks';
import { PromotionFormFields } from './PromotionFormFields';
import {
  EMPTY_PROMOTION_FORM,
  clearFieldError,
  promotionServerError,
  validatePromotionForm,
} from './promotionForm';
import type { PromotionFormErrors, PromotionFormValues } from './promotionForm';

/**
 * Crear promoción (admin). Plain controlled form: fixed-amount values are entered in USD and
 * converted to integer minor units before sending (money is never a float); percentages pass
 * through. Dates use datetime-local and are normalized to ISO. Each rule shows under its field
 * (`validatePromotionForm`); the server re-validates and its answer is placed the same way.
 */
export function PromocionCreatePage({ basePath }: { basePath: string }) {
  const navigate = useNavigate();
  const create = useCreatePromotion();
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<PromotionFormErrors>({});
  const [form, setForm] = useState<PromotionFormValues>(EMPTY_PROMOTION_FORM);

  const set = <K extends keyof PromotionFormValues>(key: K, value: PromotionFormValues[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    // Editing a field clears its own message (and the general one) until the next submit.
    setErrors((e) => clearFieldError(e, key));
    setError(null);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const result = validatePromotionForm(form, { requireFutureEnd: true });
    setErrors(result.errors);
    if (!result.payload) return;

    try {
      await create.mutateAsync(result.payload);
      navigate(basePath);
    } catch (err) {
      const { field, message } = promotionServerError(err, 'No se pudo crear la promoción.');
      if (field) setErrors({ [field]: message });
      else setError(message);
    }
  };

  return (
    <div className={`flex flex-col gap-6 ${FORM_COLUMN}`}>
      <div>
        <BackLink to={basePath}>Promociones</BackLink>
        <PageHeader title="Nueva promoción" description="Define un descuento, monto fijo o rebate." />
      </div>

      <Card>
        <CardContent className="pt-5">
          <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
            {error ? (
              <p className="rounded-md border border-danger-soft bg-danger-soft px-3 py-2 text-sm font-medium text-danger" role="alert">
                {error}
              </p>
            ) : null}

            <PromotionFormFields form={form} errors={errors} onChange={set} />

            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Creando…' : 'Crear promoción'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
